/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023.
 */

import { constructPreviewTokenHeaderRequestParams } from '@/core/data/utils/constructRequestParams';
import { Settings } from '@/data/Settings';
import { NEW_PREVIEW_SESSION_PARAM, PREVIEW_TOKEN_PARAM } from '@/data/constants/preview';
import { CurrentUser } from '@/data/forReactAndMobile/SaveCurrentUser';
import { WC_TOKEN, WC_TRUSTED_TOKEN } from '@/data/forReactAndMobile/constants';
import { AppContextWrapper } from '@/data/types/AppRouter';
import { canBeCachedByCDN } from '@/data/utils/canBeCachedByCDN';
import { getRequestId } from '@/data/utils/getRequestId';
import { isAppContext } from '@/data/utils/isAppContext';
import Cookies from 'cookies';
import { Translation } from 'integration/generated/translations';
import { GetServerSidePropsContext } from 'next';
const EMPTY_PARAMS = {};

const getDefaultRequestHeaders = ({
	context,
	settings,
	routes,
}: {
	context: GetServerSidePropsContext;
	settings?: Settings;
	routes?: Translation;
}) => {
	const _requestId = getRequestId(context);
	let passCookieHeader = true;
	if (settings && routes) {
		// if it is CDN cache enabled page, the user information is not relevant,
		// basically, in this case, we treat ourself as generic user, and
		// `passCookieHeader` will be set to false in this case.
		passCookieHeader = !canBeCachedByCDN({ context, settings, routes });
	}
	return {
		_requestId,
		...(context.req.headers.cookie && {
			headers: {
				...(passCookieHeader && {
					cookie: context.req.headers.cookie,
				}),
			},
		}),
	};
};
/**
 * Construct Server-side Request Parameters for all requests.
 * @returns Request params that contains cookie and/or preview token if applicable.
 */
export const constructRequestParamsWithPreviewToken = ({
	context,
	settings,
	routes,
}: {
	context: GetServerSidePropsContext;
	settings?: Settings;
	routes?: Translation;
}) => {
	if (isAppContext(context)) {
		return { headers: { cookie: (context as AppContextWrapper).extra.cookie } };
	} else {
		const _requestId = getRequestId(context);
		const previewToken = [context.query[PREVIEW_TOKEN_PARAM]].flat(1).at(0);
		const cookies = new Cookies(context.req, context.res);
		const Wctoken = cookies?.get('WCToken');
		const WctrustedToken = cookies?.get('WCTrustedToken');
		if (previewToken) {
			if (context.query[NEW_PREVIEW_SESSION_PARAM] === 'true') {
				// new session, not sending any existing cookie
				return {
					_requestId,
					headers: {
						[PREVIEW_TOKEN_PARAM]: previewToken,
					},
				};
			} else {
				return {
					_requestId,
					headers: {
						[PREVIEW_TOKEN_PARAM]: previewToken,
						...(context.req.headers.cookie && {
							cookie: context.req.headers.cookie,
						}),
					},
				};
			}
		} else if (Wctoken && WctrustedToken) {
			return {
				_requestId,
				headers: {
					[WC_TOKEN]: Wctoken,
					[WC_TRUSTED_TOKEN]: WctrustedToken,
					...(context.req.headers.cookie && {
						cookie: context.req.headers.cookie,
					}),
				},
			};
		} else {
			const headers = getDefaultRequestHeaders({ context, settings, routes });
			return headers;
		}
	}
};

export const constructClientPreviewTokenHeaderRequestParams = (
	previewToken: string | undefined,
	currentUserStorage?: CurrentUser | null,
	insertTokens?: boolean
) => {
	if (previewToken) {
		return {
			headers: {
				[PREVIEW_TOKEN_PARAM]: previewToken,
			},
		};
	} else if (
		currentUserStorage &&
		currentUserStorage?.WCToken &&
		currentUserStorage?.WCTrustedToken &&
		insertTokens
	) {
		return {
			headers: {
				[WC_TOKEN]: currentUserStorage.WCToken,
				[WC_TRUSTED_TOKEN]: currentUserStorage.WCTrustedToken,
			},
		};
	} else {
		return EMPTY_PARAMS;
	}
};

export { constructPreviewTokenHeaderRequestParams };
